import React, { useMemo } from 'react';
import KPICard from '../ui/KPICard';
import { useBranchSummaryData } from '../../hooks/useBrandQueries';
import { ErrorMessage } from '../ui';
import { logger } from '../../utils/logger';
import {
  BRANCH_COMPARISON_METRICS,
  findBestBranch,
  formatMetricValue,
  getKpiVariant,
  getMetricValue
} from '../../utils/brandComparisonMetrics';

/**
 * Props for the BrandKPIsSection component
 */
interface BrandKPIsSectionProps {
  /** Brand ID to fetch KPI data for */
  brandId: number;
  /** Time range for data (default: 'last6Months') */
  timeRange?: string;
}

/**
 * Brand KPIs Section Component
 *
 * Displays brand-average KPI cards aligned with the branch comparison table metrics.
 * Values come from the same `/brand/{id}/summary` endpoint used by the comparison table.
 */
const BrandKPIsSection: React.FC<BrandKPIsSectionProps> = ({
  brandId,
  timeRange = 'last6Months'
}) => {
  const { data, isLoading, error, refetch } = useBranchSummaryData(brandId, timeRange);

  const brandAverage = data?.brandAverage ?? null;
  const branches = data?.branches ?? [];
  const currency = brandAverage?.currency || branches[0]?.currency || 'تومان';

  const kpiCards = useMemo(() => {
    if (!brandAverage) return [];

    return BRANCH_COMPARISON_METRICS.map(metric => {
      const value = getMetricValue(brandAverage, metric.id);
      const bestBranch = findBestBranch(branches, metric.id);

      return {
        metric,
        value,
        formattedValue: formatMetricValue(value, metric.unit, currency),
        subtitle: bestBranch
          ? `🏆 ${bestBranch.branchName}: ${formatMetricValue(bestBranch.value, metric.unit, bestBranch.currency)}`
          : 'میانگین برند'
      };
    });
  }, [brandAverage, branches, currency]);

  if (error) {
    logger.error('Error loading brand KPIs', 'BrandKPIsSection', error);
    return (
      <ErrorMessage
        message="خطا در بارگذاری شاخص‌های کلیدی برند"
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
      {kpiCards.map(({ metric, formattedValue, subtitle }) => (
        <KPICard
          key={metric.id}
          title={metric.label}
          value={formattedValue}
          subtitle={subtitle}
          variant={getKpiVariant(metric.category)}
          accentStyle="corner"
          loading={isLoading}
          aria-label={`${metric.label}: ${formattedValue}`}
        />
      ))}
    </div>
  );
};

export default BrandKPIsSection;
